import { Tag } from '@loadsmart/loadsmart-ui'
import { Card, CardContent, Stack, Box, Link, Typography } from '@mui/material'
import { InstallationList } from 'common/types/kraken-core/Installation'
import { ROUTES } from 'routing/routes'
import { useEffect, useState } from 'react'
import { getTraffic } from 'services/kraken-core/messages/messages.service'
import { TrafficResponseWrapper } from 'common/types/kraken-core/TrafficVolume'
import moment from 'moment'
import LSDate, { DATE_FORMAT_MMDDYYHHMM } from 'common/Date.helpers'
import MessageTrafficVolumeRate from 'molecules/MessageTraffic/MessageVolumeRate'
import Loading from 'atoms/Loading'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { useTheme } from '@mui/material'

interface InstallationCardProps {
  installation: InstallationList
}

const DEFAULT_TRAFFIC_PERIOD_IN_DAYS = 7
const DEFAULT_TRAFFIC_TIMEFRAME = 'hour'

// Custom Sparkline Component for Installation Traffic
interface InstallationSparklineProps {
  traffic: TrafficResponseWrapper
}

function InstallationSparkline({ traffic }: InstallationSparklineProps) {
  const theme = useTheme()
  const [series, setSeries] = useState<any>()

  useEffect(() => {
    if (!traffic?.traffic?.buckets) {
      return
    }

    const series = new Map<string, Array<Array<any>>>()
    const contextTraffic = traffic

    contextTraffic.traffic.buckets.forEach(bucket => {
      const dateTime = new Date(bucket.datetime).getTime()
      if (!series.has('total')) {
        series.set('total', [[dateTime, bucket.count]])
      } else {
        const serie = series.get('total') as any
        series.set('total', [...serie, [dateTime, bucket.count]])
      }
    })

    const chartSeries: any = []
    series.forEach((value, key) => {
      chartSeries.push({
        name: key,
        data: value,
        color: theme.palette.primary.main,
      })
    })

    setSeries(chartSeries)
  }, [traffic, theme.palette.primary.main])

  const options = {
    chart: {
      type: 'spline',
      height: 40,
      width: 120,
      margin: [2, 0, 2, 0],
      backgroundColor: 'transparent',
      borderWidth: 0,
      shadow: false,
      style: {
        overflow: 'visible',
      },
      skipClone: true,
    },
    title: {
      text: '',
    },
    xAxis: {
      type: 'datetime',
      labels: {
        enabled: false,
      },
      title: {
        text: null,
      },
      startOnTick: false,
      endOnTick: false,
      tickPositions: [],
      lineWidth: 0,
    },
    yAxis: {
      endOnTick: false,
      startOnTick: false,
      labels: {
        enabled: false,
      },
      title: {
        text: null,
      },
      tickPositions: [0],
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      hideDelay: 0,
      outside: true,
      shared: true,
      formatter: function (this: any) {
        return `<b>${moment(this.x).format('MMM DD, HH:mm')}</b><br/>
                Messages: <b>${this.y}</b>`
      },
    },
    plotOptions: {
      series: {
        animation: true,
        lineWidth: 2,
        shadow: false,
        states: {
          hover: {
            lineWidth: 2,
          },
        },
        marker: {
          enabled: false,
        },
        fillOpacity: 0.25,
        turboThreshold: 5000,
        dataGrouping: {
          enabled: true,
        },
      },
    },
    credits: {
      enabled: false,
    },
    series: series || [
      {
        name: 'no-data',
        data: [],
        color: theme.palette.primary.main,
      },
    ],
  }

  return <HighchartsReact highcharts={Highcharts} options={options} />
}

export default function InstallationCard({ installation }: InstallationCardProps) {
  const [traffic, setTraffic] = useState<TrafficResponseWrapper>({
    isLoading: false,
    traffic: {
      buckets: [],
      count: 0,
      from_date: '',
      to_date: '',
      timeframe: DEFAULT_TRAFFIC_TIMEFRAME,
    },
  })

  const fetchTraffic = (installationId: string) => {
    setTraffic((prev: any) => ({ ...prev, isLoading: true }))
    getTraffic({
      filters: { installationId: installationId },
      from_date: moment()
        .subtract(DEFAULT_TRAFFIC_PERIOD_IN_DAYS, 'days')
        .startOf('day')
        .toISOString(),
      to_date: '',
      timeframe: DEFAULT_TRAFFIC_TIMEFRAME,
    })
      .then(response => {
        response.response && setTraffic({ traffic: response.response.data, isLoading: false })
      })
      .catch(error => {
        console.error('Error getting traffic:', error)
        setTraffic((prev: any) => ({ ...prev, isLoading: false }))
      })
      .finally(() => {
        setTraffic((prev: any) => ({ ...prev, isLoading: false }))
      })
  }

  useEffect(() => {
    if (installation?.id) {
      fetchTraffic(installation.id)
    }
  }, [installation?.id])

  const renderCapabilities = (inst: InstallationList) => {
    try {
      const capabilities = (inst as any)?.capabilities
      if (!capabilities || !Array.isArray(capabilities) || capabilities.length === 0) {
        return null
      }

      const firstTwo = capabilities.slice(0, 2)
      const names = firstTwo.map((cap: any) => {
        if (typeof cap === 'string') return cap
        if (cap && typeof cap === 'object' && cap.name) return cap.name
        return 'Unknown'
      })

      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {names.map((name: string, index: number) => (
            <Tag key={index} variant="default" size="small">
              {name}
            </Tag>
          ))}
          {capabilities.length > 2 && (
            <Tag variant="default" size="small">
              ...
            </Tag>
          )}
        </Box>
      )
    } catch (error) {
      console.error('Error rendering capabilities:', error, inst)
      return null
    }
  }

  const renderTrafficVolume = () => {
    if (traffic.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <Loading />
        </Box>
      )
    }

    if (!traffic.traffic || traffic.traffic.buckets.length === 0) {
      return (
        <Box sx={{ py: 1 }}>
          <Typography variant="caption" color="textSecondary">
            No traffic in the last {DEFAULT_TRAFFIC_PERIOD_IN_DAYS} days
          </Typography>
        </Box>
      )
    }

    return (
      <Box sx={{ py: 1 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ flex: 1 }}>
            <MessageTrafficVolumeRate traffic={traffic} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InstallationSparkline traffic={traffic} />
          </Box>
        </Stack>
      </Box>
    )
  }

  return (
    <Card elevation={1}>
      <CardContent>
        <Stack spacing={2}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Link
              href={`${ROUTES.INSTALLATIONS}/${installation.id}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {installation.name}
            </Link>
            <Stack spacing={1} alignItems="flex-end">
              <Stack direction="row" spacing={2} alignItems="center">
                <Stack spacing={0.5} alignItems="flex-end">
                  <Typography variant="caption" color="textSecondary">
                    Created At
                  </Typography>
                  <Typography variant="body2" fontSize="0.75rem">
                    {LSDate(installation.created_at)?.format(DATE_FORMAT_MMDDYYHHMM)}
                  </Typography>
                </Stack>
                <Stack spacing={0.5} alignItems="flex-end">
                  <Typography variant="caption" color="textSecondary">
                    Updated At
                  </Typography>
                  <Typography variant="body2" fontSize="0.75rem">
                    {LSDate(installation.updated_at)?.format(DATE_FORMAT_MMDDYYHHMM)}
                  </Typography>
                </Stack>
                {installation.is_active ? (
                  <Tag variant="accent">Active</Tag>
                ) : (
                  <Tag variant="warning">Inactive</Tag>
                )}
              </Stack>
            </Stack>
          </Box>

          {renderCapabilities(installation)}

          <Box>
            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
              Traffic Volume (Last {DEFAULT_TRAFFIC_PERIOD_IN_DAYS} days)
            </Typography>
            {renderTrafficVolume()}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}
